/**
 * Canonical R9 `no-component-appearance-override` regex + allowlists.
 *
 * Single source of truth for BOTH:
 *   - Layer 3 (runtime):   `stripPresetConflicts()` in `./preset-utils.ts`
 *   - Layer 2 (validator): `.claude/skills/cogo-ui/tools/validate.cjs` (R9 block)
 *
 * The validator is NOT a TypeScript module — it is a `.cjs` file loaded by the
 * cogo-ui skill. To keep the two layers aligned we:
 *
 *   1. Define the canonical regex sources as string constants here.
 *   2. Export compiled `RegExp` instances for the runtime to consume directly.
 *   3. Mirror the exact same string literals inside `validate.cjs` (Option B).
 *   4. Ship a test (`__tests__/validate-r9-parity.test.js`) that reads this
 *      file, extracts the string sources, and asserts `validate.cjs` uses the
 *      same literals. If you update this file, the parity test will tell you
 *      exactly what to mirror into `validate.cjs`.
 *
 * WHY THE BROADER SCOPE?
 * Previous state had divergent regexes. Layer 2 allowed
 * `transition-* / duration-* / ease-* / outline-*` through and rejected
 * `text-card / text-popover / text-sidebar`. Layer 3 already stripped those
 * utilities and allowed those text-colors at runtime. Result: blocks passed
 * validation then got silently modified at render time, producing the
 * "regenerate doesn't help" loop. Layer 3 is what users see, so Layer 2 now
 * enforces the same contract.
 *
 * If you need to broaden the allowlist, update ONLY the `_SOURCE` constants
 * below — everything else derives from them.
 */

// ---- canonical regex sources (string form, exported for parity tests) ----

export const APPEARANCE_UTILITY_REGEX_SOURCE =
  "^(?:bg-(?!transparent$|inherit$|current$)|border(?:-|$)|rounded-|p-|px-|py-|pt-|pr-|pb-|pl-|text-(?:sm|xs|base|lg|xl|2xl)|font-(?:medium|semibold|bold|normal)|shadow-|ring-|transition-|duration-|ease-|outline-)";

export const ALLOWED_TEXT_COLOR_REGEX_SOURCE =
  "^text-(?:foreground|muted-foreground|primary|destructive|secondary|accent|card|popover|sidebar)";

// Input-specific contract — mirrors validate.cjs `input.prohibited-className`.
// shadcn <input> is renderer-managed; only layout/sizing tokens may pass
// through. Appearance/sizing/focus/shadow/etc. fight the preset. Responsive
// and state prefixes (`sm:`, `hover:`, `focus-visible:` …) are stripped
// BEFORE matching so both `w-full` and `sm:w-full` resolve to the same bare
// token.
export const INPUT_PREFIX_STRIP_REGEX_SOURCE =
  "^(?:sm:|md:|lg:|xl:|2xl:|hover:|focus:|focus-visible:|focus-within:|active:|disabled:|placeholder:|dark:|group-hover:|group-focus:|peer-hover:|style-lyra:|style-mira:)+";

export const INPUT_ALLOWED_TOKEN_REGEX_SOURCE =
  "^(?:w-full|max-w-[\\w.\\-\\[\\]\\/%]+|flex-1|mt-[\\w.\\-\\[\\]\\/%]+|mb-[\\w.\\-\\[\\]\\/%]+)$";

// Nav contract — mirrors validate.cjs `nav.logoSection-w-full` (V1) and
// `nav.layout-not-grid` (V5). The nav block in design-system.json#components.nav
// pins two structural invariants that LLM regeneration repeatedly violated
// before these rules existed:
//
//   V1 — logo/brand containers inside a nav ancestor must be shrink-0, not
//        w-full, otherwise the brand consumes the whole row and the
//        center/right slots collapse to width 0.
//   V5 — the 3-slot nav container (left/center/right) must use
//        "grid grid-cols-[auto_1fr_auto]". "flex justify-between" works for 2
//        slots but folds the center onto one side once a third slot appears.
//
// Both id matches are case-insensitive and anchored — they fire on
// well-defined ids only (logo/brand/header-left for V1, *-nav/*-topnav/*-header
// for V5) so unrelated containers that happen to share substrings never
// false-positive.

export const NAV_LOGO_SECTION_ID_REGEX_SOURCE =
  "^(?:nav-left|brand-section|logo-container|topnav-left|brand-wrapper|brand-container|header-left)$";

export const NAV_ANCESTOR_ID_REGEX_SOURCE =
  "(?:^|-)(?:nav|topnav|shop-header|admin-topnav|pm-topnav|dash-topnav|main-nav)$";

// ---- compiled RegExp instances for runtime consumption ----

export const APPEARANCE_UTILITY_REGEX = new RegExp(APPEARANCE_UTILITY_REGEX_SOURCE);
export const ALLOWED_TEXT_COLOR_REGEX = new RegExp(ALLOWED_TEXT_COLOR_REGEX_SOURCE);
export const INPUT_PREFIX_STRIP_REGEX = new RegExp(INPUT_PREFIX_STRIP_REGEX_SOURCE);
export const INPUT_ALLOWED_TOKEN_REGEX = new RegExp(INPUT_ALLOWED_TOKEN_REGEX_SOURCE);
export const NAV_LOGO_SECTION_ID_REGEX = new RegExp(NAV_LOGO_SECTION_ID_REGEX_SOURCE, "i");
export const NAV_ANCESTOR_ID_REGEX = new RegExp(NAV_ANCESTOR_ID_REGEX_SOURCE, "i");

// ---- shadcn components that honor the R9 contract ----
//
// These are the component types whose className gets stripped at runtime
// (via `stripPresetConflicts`) and flagged by the validator (R9).
//
// As of 2026-04-16 Wave 2, all 18 allowlisted renderers call
// `stripPresetConflicts` for Layer 2/3 parity. The default blocklist mode
// applies to 17 of them; `input` uses the narrower allowlist mode.

export const SHADCN_PRESET_COMPONENTS: readonly string[] = [
  "badge",
  "breadcrumb",
  "button",
  "card",
  "checkbox",
  "collapsible",
  "data-table",
  "date-picker",
  "dialog",
  "empty",
  "input",
  "month-picker",
  "radio",
  "select",
  "sidebar",
  "switch",
  "tab-trigger",
  "textarea",
];

/**
 * Returns true if `bareToken` is an appearance utility that would be stripped
 * by the runtime / flagged by the validator. `bareToken` should already have
 * responsive/state prefixes (`sm:`, `hover:`, etc.) removed.
 */
export function isAppearanceUtility(bareToken: string): boolean {
  if (!APPEARANCE_UTILITY_REGEX.test(bareToken)) return false;
  if (ALLOWED_TEXT_COLOR_REGEX.test(bareToken)) return false;
  return true;
}

/**
 * Returns true if `token` is in the narrow input allowlist (layout/spacing
 * only). Responsive/state prefixes are stripped before matching so
 * `sm:w-full` resolves to `w-full` and stays allowed.
 */
export function isInputAllowedToken(token: string): boolean {
  const bare = token.replace(INPUT_PREFIX_STRIP_REGEX, "");
  return INPUT_ALLOWED_TOKEN_REGEX.test(bare);
}
